import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config';

const DEFAULT_BATCHES = [
  'Lead 100 Kč - nadiktované podklady',
  'Lead 300 Kč - vyúčtování k dispozici'
];

const isClosedForecast = (forecast) => {
  const normalized = String(forecast || '').trim();
  return normalized === '100' || normalized === '100%' || normalized === '100.0' || parseInt(normalized, 10) === 100;
};

const getMonthLabel = (month) => {
  if (!month) return '';
  const date = new Date(`${month}-01`);
  return date.toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
};

const getMonthsBetween = (fromMonth, toMonth) => {
  if (!fromMonth || !toMonth) return [];

  let start = new Date(`${fromMonth}-01`);
  let end = new Date(`${toMonth}-01`);
  if (start > end) {
    const temp = start;
    start = end;
    end = temp;
  }

  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

const formatBatchLabel = (batchName) => {
  if (batchName === 'Lead 100 Kč - nadiktované podklady') return '100 Kč';
  if (batchName === 'Lead 300 Kč - vyúčtování k dispozici') return '300 Kč';
  return batchName;
};

export default function LeadSuccessStatsModal({ isDarkMode, onClose, currentUserId, initialMonth, batchOptions = [] }) {
  const availableBatches = useMemo(() => {
    const fromProps = (batchOptions || []).map(b => b.batchName).filter(Boolean);
    return [...new Set([...DEFAULT_BATCHES, ...fromProps])];
  }, [batchOptions]);

  const [fromMonth, setFromMonth] = useState(initialMonth || '');
  const [toMonth, setToMonth] = useState(initialMonth || '');
  const [selectedBatches, setSelectedBatches] = useState(DEFAULT_BATCHES);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, closed: 0, users: 0, months: 0 });
  const [error, setError] = useState('');

  useEffect(() => {
    if (availableBatches.length > 0) {
      setSelectedBatches(prev => (prev.length > 0 ? prev.filter(batch => availableBatches.includes(batch)) : availableBatches));
    }
  }, [availableBatches]);

  const loadStats = async () => {
    const months = getMonthsBetween(fromMonth, toMonth);
    if (months.length === 0) {
      setError('Vyberte prosím rozsah měsíců.');
      setRows([]);
      return;
    }

    if (selectedBatches.length === 0) {
      setError('Vyberte alespoň jednu šarži.');
      setRows([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const monthResults = await Promise.all(
        months.map(async (monthKey) => {
          const [year, month] = monthKey.split('-');
          const response = await fetch(
            `${API_URL}/leads-assignments?month=${parseInt(month, 10)}&year=${parseInt(year, 10)}&userId=${currentUserId || ''}`
          );
          const data = response.ok ? await response.json() : [];
          return { monthKey, data: Array.isArray(data) ? data : [] };
        })
      );

      const rowMap = new Map();
      let total = 0;
      let closed = 0;

      monthResults.forEach(({ monthKey, data }) => {
        data
          .filter(item => selectedBatches.includes(item.batchName))
          .forEach(item => {
            const userId = String(item.toUserId || item.userId || item.toUserName || 'unknown');
            const userName = item.toUserName || item.userName || 'Neznámý';
            const rowKey = userId;

            if (!rowMap.has(rowKey)) {
              rowMap.set(rowKey, {
                userId,
                userName,
                total: 0,
                closed: 0,
                months: {},
                batches: {}
              });
            }

            const row = rowMap.get(rowKey);
            row.total += 1;
            total += 1;

            const isClosed = isClosedForecast(item.forecast);
            if (isClosed) {
              row.closed += 1;
              closed += 1;
            }

            if (!row.months[monthKey]) {
              row.months[monthKey] = { total: 0, closed: 0 };
            }
            row.months[monthKey].total += 1;
            if (isClosed) {
              row.months[monthKey].closed += 1;
            }

            if (!row.batches[item.batchName]) {
              row.batches[item.batchName] = { total: 0, closed: 0 };
            }
            row.batches[item.batchName].total += 1;
            if (isClosed) {
              row.batches[item.batchName].closed += 1;
            }
          });
      });

      const sortedRows = Array.from(rowMap.values()).sort((a, b) => {
        const rateA = a.total > 0 ? a.closed / a.total : 0;
        const rateB = b.total > 0 ? b.closed / b.total : 0;
        if (rateA !== rateB) return rateB - rateA;
        return b.total - a.total;
      });

      setRows(sortedRows);
      setSummary({
        total,
        closed,
        users: new Set(sortedRows.map(row => row.userId)).size,
        months: months.length
      });
    } catch (err) {
      console.error('Chyba při načítání úspěšnosti leadů:', err);
      setError('Nepodařilo se načíst statistiky.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fromMonth && toMonth) {
      loadStats();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalRate = summary.total > 0 ? Math.round((summary.closed / summary.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className={`w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex flex-col ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
        <div className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className="text-xl font-bold">📈 Úspěšnost OZ podle období</h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Souhrn za všechny měsíce ve vybraném období, seřazeno podle úspěšnosti.
            </p>
          </div>
          <button onClick={onClose} className={`text-2xl leading-none ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
        </div>

        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-900/20' : 'border-gray-100 bg-gray-50'}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Od měsíce</label>
              <input
                type="month"
                value={fromMonth}
                onChange={(e) => setFromMonth(e.target.value)}
                className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Do měsíce</label>
              <input
                type="month"
                value={toMonth}
                onChange={(e) => setToMonth(e.target.value)}
                className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div className="flex-1 min-w-[280px]">
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Šarže</label>
              <div className={`flex flex-wrap gap-2 rounded border p-3 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                {availableBatches.map(batchName => {
                  const active = selectedBatches.includes(batchName);
                  return (
                    <button
                      key={batchName}
                      onClick={() => setSelectedBatches(prev => prev.includes(batchName) ? prev.filter(item => item !== batchName) : [...prev, batchName])}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? 'bg-indigo-600 border-indigo-600 text-white' : isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-600' : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                    >
                      {formatBatchLabel(batchName)}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              onClick={loadStats}
              disabled={loading}
              className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              {loading ? 'Načítám...' : 'Načíst'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Celkem leadů</div>
              <div className="text-2xl font-bold mt-1">{summary.total}</div>
            </div>
            <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Uzavřeno</div>
              <div className="text-2xl font-bold mt-1 text-green-500">{summary.closed}</div>
            </div>
            <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Úspěšnost</div>
              <div className="text-2xl font-bold mt-1 text-indigo-500">{totalRate} %</div>
            </div>
            <div className={`rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Období</div>
              <div className="text-sm font-semibold mt-1">{summary.months} měsíců / {summary.users} OZ</div>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className={`py-16 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Načítám statistiky...
            </div>
          ) : rows.length === 0 ? (
            <div className={`py-16 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Pro zvolený filtr nejsou k dispozici žádná data.
            </div>
          ) : (
            <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full text-sm">
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-4 py-3 text-left font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>OZ</th>
                    <th className={`px-4 py-3 text-center font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Leady</th>
                    <th className={`px-4 py-3 text-center font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Uzavřeno</th>
                    <th className={`px-4 py-3 text-center font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Úspěšnost</th>
                    <th className={`px-4 py-3 text-left font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíce v období</th>
                    <th className={`px-4 py-3 text-left font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Šarže</th>
                  </tr>
                </thead>
                <tbody className={isDarkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
                  {rows.map((row) => {
                    const rate = row.total > 0 ? Math.round((row.closed / row.total) * 100) : 0;
                    const monthText = Object.entries(row.months)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([monthKey, stats]) => {
                        const monthRate = stats.total > 0 ? Math.round((stats.closed / stats.total) * 100) : 0;
                        return `${getMonthLabel(monthKey)} ${stats.closed}/${stats.total} (${monthRate} %)`;
                      })
                      .join(' • ');
                    const batchText = Object.entries(row.batches)
                      .map(([batchName, stats]) => `${formatBatchLabel(batchName)} ${stats.closed}/${stats.total}`)
                      .join(' • ');

                    return (
                      <tr key={row.userId} className={isDarkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'}>
                        <td className="px-4 py-3">{row.userName}</td>
                        <td className="px-4 py-3 text-center font-semibold">{row.total}</td>
                        <td className="px-4 py-3 text-center font-semibold text-green-500">{row.closed}</td>
                        <td className={`px-4 py-3 text-center font-bold ${rate >= 50 ? 'text-green-500' : rate >= 25 ? 'text-yellow-500' : 'text-red-500'}`}>{rate} %</td>
                        <td className={`px-4 py-3 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{monthText}</td>
                        <td className={`px-4 py-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{batchText}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}